import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ChevronLeft, Phone, Mail, Globe, MapPin, Copy, ExternalLink,
  PhoneCall, ClipboardList, StickyNote, Archive, ArchiveRestore, Trash2, CheckCircle2,
  Loader2, FileSearch, Sparkles, RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { formatApiErrorDetail } from "@/lib/apiClient";

const CONTACT_STATUS = "Contacted";

export default function LeadProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statuses, setStatuses] = useState([]);
  const [note, setNote] = useState("");
  const [followUp, setFollowUp] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, facets] = await Promise.all([
        api.get(`/admin/leads/${id}`),
        api.get("/admin/leads/facets"),
      ]);
      setLead(data);
      setFollowUp(data.next_follow_up ? data.next_follow_up.slice(0, 16) : "");
      setStatuses(facets.data.statuses);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
      nav("/admin/dashboard/leads");
    } finally {
      setLoading(false);
    }
  }, [id, nav]);

  const [generating, setGenerating] = useState(false);
  const generateOutreach = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/admin/leads/${id}/generate-outreach`);
      toast.success("Outreach drafts ready.");
      setLead((l) => ({ ...l, outreach_drafts: data.drafts, outreach_generated_at: data.generated_at }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setGenerating(false);
    }
  };

  const copyText = async (v, label) => {
    if (!v) return;
    try {
      await navigator.clipboard.writeText(v);
      toast.success(`${label} copied.`);
    } catch {
      toast.error("Copy failed");
    }
  };

  useEffect(() => { load(); }, [load]);

  const patch = async (patch) => {
    setSaving(true);
    try {
      await api.patch(`/admin/leads/${id}`, patch);
      await load();
      toast.success("Updated.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const copy = async (v, label) => {
    if (!v) return;
    try {
      await navigator.clipboard.writeText(v);
      toast.success(`${label} copied.`);
    } catch {
      toast.error("Copy failed");
    }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await api.post(`/admin/leads/${id}/notes`, { text: note });
      setNote("");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const setFollowUpDate = async () => {
    await patch({ next_follow_up: followUp ? new Date(followUp).toISOString() : null });
  };

  const doDelete = async () => {
    if (!window.confirm("Permanently delete this lead? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/leads/${id}`);
      toast.success("Lead deleted.");
      nav("/admin/dashboard/leads");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const toggleArchive = async () => {
    try {
      const endpoint = lead.archived ? "restore" : "archive";
      await api.post(`/admin/leads/${id}/${endpoint}`);
      await load();
      toast.success(lead.archived ? "Restored." : "Archived.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  if (loading || !lead) {
    return (
      <div data-testid="lead-loading" className="text-white/50 text-sm">
        <Loader2 className="inline-block mr-2 animate-spin" size={14} /> Loading lead…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="lead-profile-page">
      <div className="flex items-center gap-3">
        <Link to="/admin/dashboard/leads" className="text-white/50 hover:text-white transition-colors inline-flex items-center gap-1 text-sm">
          <ChevronLeft size={14} /> Back to CRM
        </Link>
      </div>

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div className="min-w-0">
          <span className="text-[11px] tracking-[0.22em] uppercase text-blue-400 font-medium">
            {lead.lead_source || "Manual"}
          </span>
          <h1 className="font-display text-3xl md:text-4xl mt-2 text-white truncate">
            {lead.business_name || lead.contact_name || lead.email || "Untitled lead"}
          </h1>
          <p className="text-white/50 text-sm mt-1">
            Added {lead.created_at ? new Date(lead.created_at).toLocaleString() : "—"}
            {lead.archived && <span className="ml-2 text-amber-300">· Archived</span>}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => patch({ status: CONTACT_STATUS })} disabled={saving} className="bg-blue-500 hover:bg-blue-600 text-white rounded-full h-10 px-4" data-testid="mark-contacted-btn">
            <PhoneCall size={14} className="mr-1.5" /> Mark contacted
          </Button>
          <Button variant="outline" onClick={toggleArchive} className="bg-transparent border-white/15 hover:bg-white/5 text-white rounded-full h-10 px-4" data-testid="archive-btn">
            {lead.archived ? <><ArchiveRestore size={14} className="mr-1.5" /> Restore</> : <><Archive size={14} className="mr-1.5" /> Archive</>}
          </Button>
          <Button variant="outline" onClick={doDelete} className="bg-red-500/10 border-red-500/30 hover:bg-red-500/20 text-red-200 rounded-full h-10 px-4" data-testid="delete-btn">
            <Trash2 size={14} className="mr-1.5" /> Delete
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: contact info + website */}
        <div className="lg:col-span-2 space-y-6">
          <div className="card-soft rounded-2xl p-6">
            <h3 className="font-display text-xl text-white">Contact</h3>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <InfoRow icon={Phone} label="Phone" value={lead.phone} onCopy={() => copy(lead.phone, "Phone")} href={lead.phone ? `tel:${lead.phone}` : undefined} testid="info-phone" />
              <InfoRow icon={Mail} label="Email" value={lead.email} onCopy={() => copy(lead.email, "Email")} href={lead.email ? `mailto:${lead.email}` : undefined} testid="info-email" />
              <InfoRow icon={Globe} label="Website" value={lead.website} onCopy={() => copy(lead.website, "Website")} href={lead.website && (lead.website.startsWith("http") ? lead.website : `https://${lead.website}`)} testid="info-website" />
              <InfoRow icon={MapPin} label="Location" value={[lead.city, lead.state].filter(Boolean).join(", ") || null} testid="info-location" />
            </div>
          </div>

          {lead.website && (
            <div className="card-soft rounded-2xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-display text-xl text-white">Website preview</h3>
                  <p className="text-xs text-white/45 mt-0.5">Some sites block embedding. If empty, open in a new tab.</p>
                </div>
                <a href={lead.website.startsWith("http") ? lead.website : `https://${lead.website}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-white/15 hover:border-white/30 text-white/80 hover:text-white transition-colors">
                  Open in new tab <ExternalLink size={12} />
                </a>
              </div>
              <div className="mt-4 rounded-lg overflow-hidden border border-white/10 bg-white/[0.02]">
                <iframe
                  title="preview"
                  src={lead.website.startsWith("http") ? lead.website : `https://${lead.website}`}
                  className="w-full h-[480px] bg-white"
                  sandbox="allow-same-origin allow-scripts"
                />
              </div>
            </div>
          )}

          <div className="card-soft rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-xl text-white">Activity timeline</h3>
              <FileSearch size={16} className="text-white/30" />
            </div>
            {(!lead.activity || lead.activity.length === 0) ? (
              <div className="text-sm text-white/40">No activity yet. Contact this lead or add a note.</div>
            ) : (
              <ul className="space-y-4">
                {lead.activity.map((a, i) => (
                  <li key={i} className="border-l-2 border-blue-500/40 pl-4">
                    <div className="text-sm text-white">{formatActivity(a)}</div>
                    <div className="text-xs text-white/40 mt-0.5">{new Date(a.at).toLocaleString()} · {a.by || "—"}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Right: status / follow-up / notes */}
        <div className="space-y-6">
          <div className="card-soft rounded-2xl p-6">
            <h3 className="font-display text-lg text-white mb-4">Pipeline stage</h3>
            <Select value={lead.status || "New Lead"} onValueChange={(v) => patch({ status: v })}>
              <SelectTrigger className="bg-[#0A0A0A] border-white/15 text-white h-11" data-testid="lead-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0F0F0F] border-white/10 text-white max-h-72">
                {statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>

            <div className="mt-5 grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={() => patch({ status: "Closed Won" })} className="bg-emerald-500/10 border-emerald-500/30 hover:bg-emerald-500/20 text-emerald-200 rounded-md h-10" data-testid="mark-won-btn">
                <CheckCircle2 size={13} className="mr-1.5" /> Won
              </Button>
              <Button variant="outline" onClick={() => patch({ status: "Closed Lost" })} className="bg-red-500/10 border-red-500/30 hover:bg-red-500/20 text-red-200 rounded-md h-10" data-testid="mark-lost-btn">
                Lost
              </Button>
            </div>
          </div>

          <div className="card-soft rounded-2xl p-6">
            <h3 className="font-display text-lg text-white mb-4 inline-flex items-center gap-2"><ClipboardList size={16} /> Follow-up</h3>
            <Input type="datetime-local" value={followUp} onChange={(e) => setFollowUp(e.target.value)} className="bg-[#0A0A0A] border-white/15 text-white h-10" data-testid="followup-input" />
            <Button onClick={setFollowUpDate} disabled={saving} className="mt-3 w-full bg-blue-500 hover:bg-blue-600 text-white rounded-md h-10" data-testid="followup-save">
              Save follow-up date
            </Button>
            {lead.next_follow_up && (
              <p className="text-xs text-white/50 mt-3">
                Currently: <span className="text-blue-300">{new Date(lead.next_follow_up).toLocaleString()}</span>
              </p>
            )}
          </div>

          <div className="card-soft rounded-2xl p-6">
            <h3 className="font-display text-lg text-white mb-4 inline-flex items-center gap-2"><StickyNote size={16} /> Notes</h3>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4} placeholder="Add a note about this lead…" className="bg-[#0A0A0A] border-white/15 text-white" data-testid="note-input" />
            <Button onClick={addNote} disabled={!note.trim()} className="mt-3 w-full bg-blue-500 hover:bg-blue-600 text-white rounded-md h-10 disabled:opacity-40" data-testid="note-save">
              Add note
            </Button>
          </div>

          <div className="card-soft rounded-2xl p-6" data-testid="ai-outreach-card">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-display text-lg text-white inline-flex items-center gap-2">
                  <Sparkles size={16} className="text-blue-300" /> AI outreach drafts
                </h3>
                <p className="text-xs text-white/50 mt-1">
                  Cold-call opener, email &amp; DM — grounded only in verified info.
                </p>
              </div>
              <Button size="sm" onClick={generateOutreach} disabled={generating} className="bg-blue-500 hover:bg-blue-600 text-white rounded-md h-9" data-testid="generate-outreach-btn">
                {generating ? <Loader2 size={13} className="mr-1.5 animate-spin" /> :
                  lead.outreach_drafts ? <RefreshCw size={13} className="mr-1.5" /> : <Sparkles size={13} className="mr-1.5" />}
                {lead.outreach_drafts ? "Regenerate" : "Generate"}
              </Button>
            </div>

            {lead.outreach_drafts ? (
              <div className="mt-5 space-y-5">
                {lead.outreach_generated_at && (
                  <div className="text-[10px] uppercase tracking-[0.18em] text-white/35">
                    Generated {new Date(lead.outreach_generated_at).toLocaleString()}
                  </div>
                )}
                {lead.outreach_drafts.outreach_angle && (
                  <DraftBlock label="Outreach angle" text={lead.outreach_drafts.outreach_angle} onCopy={() => copyText(lead.outreach_drafts.outreach_angle, "Angle")} testid="draft-angle" />
                )}
                {lead.outreach_drafts.opener && (
                  <DraftBlock label="Cold-call opener" text={lead.outreach_drafts.opener} onCopy={() => copyText(lead.outreach_drafts.opener, "Opener")} testid="draft-opener" />
                )}
                {(lead.outreach_drafts.email_subject || lead.outreach_drafts.email_body) && (
                  <DraftBlock
                    label="Email draft"
                    text={`Subject: ${lead.outreach_drafts.email_subject || ""}\n\n${lead.outreach_drafts.email_body || ""}`.trim()}
                    onCopy={() => copyText(`Subject: ${lead.outreach_drafts.email_subject}\n\n${lead.outreach_drafts.email_body}`, "Email")}
                    testid="draft-email"
                  />
                )}
                {lead.outreach_drafts.dm_draft && (
                  <DraftBlock label="Instagram / DM" text={lead.outreach_drafts.dm_draft} onCopy={() => copyText(lead.outreach_drafts.dm_draft, "DM")} testid="draft-dm" />
                )}
                {Array.isArray(lead.outreach_drafts.follow_up_questions) && lead.outreach_drafts.follow_up_questions.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-white/40">Discovery questions</div>
                    <ul className="mt-2 space-y-1.5">
                      {lead.outreach_drafts.follow_up_questions.map((q, i) => (
                        <li key={i} className="text-sm text-white/75 pl-3 border-l-2 border-blue-500/30">{q}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {lead.outreach_drafts.website_problem_summary && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-white/40">Website problem summary</div>
                    <p className="mt-2 text-sm text-white/70 leading-relaxed">{lead.outreach_drafts.website_problem_summary}</p>
                  </div>
                )}
                {lead.outreach_drafts.score_explanation && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-white/40">Score explanation</div>
                    <p className="mt-2 text-sm text-white/70 leading-relaxed">{lead.outreach_drafts.score_explanation}</p>
                  </div>
                )}
                <div className="text-[11px] text-white/35 border-t border-white/10 pt-4">
                  These are drafts. Review before sending — nothing is auto-sent.
                </div>
              </div>
            ) : (
              <div className="mt-5 text-sm text-white/45">
                Click <span className="text-white">Generate</span> to draft an opener, personalized email, DM, and discovery questions from the verified info on this lead.
              </div>
            )}
          </div>

          {(lead.lead_score != null || lead.recommended_service) && (
            <div className="card-soft rounded-2xl p-6" data-testid="lead-score-card">
              <div className="flex items-center justify-between">
                <h3 className="font-display text-lg text-white">Lead score</h3>
                {lead.lead_label && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full border border-white/15 text-white/70 uppercase tracking-wide">
                    {lead.lead_label}
                  </span>
                )}
              </div>
              <div className="mt-4 flex items-baseline gap-3">
                <span className="font-display text-5xl text-white">{lead.lead_score ?? "—"}</span>
                <span className="text-xs text-white/50">/ 100</span>
              </div>
              {lead.recommended_service && (
                <div className="mt-4">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-blue-300">Recommended service</div>
                  <div className="text-sm text-white mt-1">{lead.recommended_service}</div>
                </div>
              )}
              {lead.score_explanation && (
                <p className="text-xs text-white/55 mt-4 leading-relaxed">{lead.score_explanation}</p>
              )}
              {Array.isArray(lead.issues) && lead.issues.length > 0 && (
                <ul className="mt-4 space-y-1.5">
                  {lead.issues.map((iss, i) => (
                    <li key={i} className="text-xs text-red-200/70">• {iss}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value, onCopy, href, testid }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg border border-white/10 bg-white/[0.02]" data-testid={testid}>
      <span className="h-9 w-9 rounded-lg border border-white/10 bg-white/[0.03] flex items-center justify-center text-blue-300 shrink-0">
        <Icon size={15} />
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.18em] text-white/40">{label}</div>
        <div className="text-sm text-white truncate">
          {value ? (
            href ? <a href={href} target="_blank" rel="noopener noreferrer" className="hover:text-blue-300">{value}</a> : value
          ) : "—"}
        </div>
      </div>
      {value && onCopy && (
        <button onClick={onCopy} className="text-white/40 hover:text-white transition-colors" title="Copy">
          <Copy size={13} />
        </button>
      )}
    </div>
  );
}

function formatActivity(a) {
  if (a.type === "status_changed") return `Status changed: ${a.from || "—"} → ${a.to}`;
  if (a.type === "note") return `Note: ${a.text}`;
  if (a.type === "created") return a.text || "Lead created";
  if (a.type === "audit_re_requested") return a.text || "Audit re-requested";
  if (a.type === "outreach_generated") return a.text || "AI outreach drafts generated";
  return `${a.type}${a.text ? `: ${a.text}` : ""}`;
}

function DraftBlock({ label, text, onCopy, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[10px] uppercase tracking-[0.18em] text-white/40">{label}</div>
        <button onClick={onCopy} className="text-white/40 hover:text-white transition-colors" title="Copy">
          <Copy size={12} />
        </button>
      </div>
      <p className="text-sm text-white/85 leading-relaxed whitespace-pre-wrap rounded-lg border border-white/10 bg-white/[0.02] p-3">
        {text}
      </p>
    </div>
  );
}
